import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import {
  IconButton,
  Grid,
  Card as MuiCard,
  CardContent,
  Divider as MuiDivider,
  Typography as MuiTypography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import EditIcon from "@mui/icons-material/Edit";
import styled from "styled-components/macro";
import { spacing } from "@mui/system";
import { api } from "../../../services/api";
import { Client } from "../../../config/app-info";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Typography = styled(MuiTypography)(spacing);

const WrapperDataGrid = styled.div`
  width: 100%;
`;

const ContentDataGrid = styled.div`
  height: 48vh;
  overflow-y: auto;
`;

type Props = {
  section: string;
};

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbar />
    </GridToolbarContainer>
  );
}
const ClientsList: React.FC<Props> = ({ section }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await api.get("api/clients");

        if (response.status === 200) {
          setClients(response.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchClients();
  }, []);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.3,
      type: "string",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "cnpj",
      headerName: "CNPJ",
      flex: 0.8,
      type: "string",
    },
    {
      field: "name",
      headerName: "Nome",
      flex: 1.5,
      type: "string",
    },
    {
      field: "nameResponsible",
      headerName: "Nome do responsável",
      flex: 1.2,
      type: "string",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.4,
      type: "string",
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => (params.value ? "Ativo" : "Inativo"),
    },
    {
      field: "createdAt",
      headerName: "Criado em",
      flex: 0.8,
      type: "string",
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => {
        const date = new Date(String(params.value));
        return `${date.toLocaleDateString("pt-br")} ${date.toLocaleTimeString(
          "pt-br"
        )}`;
      },
    },
    {
      field: "actions",
      headerName: "Editar",
      width: 50,
      flex: 0.3,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <IconButton
          edge="end"
          aria-label="editar"
          onClick={() => navigate(`/empresas/editar/${params.row.id}/`)}
          size="large"
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{section}</title>
      </Helmet>
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid item>
          <Typography variant="h3" gutterBottom>
            {section}
          </Typography>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Card>
        <CardContent>
          <WrapperDataGrid>
            <ContentDataGrid>
              <DataGrid
                rows={clients}
                columns={columns}
                components={{ Toolbar: CustomToolbar }}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      id: !isMobile,
                      cnpj: !isMobile,
                      nameResponsible: !isMobile,
                      createdAt: !isMobile,
                    },
                  },
                }}
              />
            </ContentDataGrid>
          </WrapperDataGrid>
        </CardContent>
      </Card>
    </>
  );
};

export default ClientsList;
